import React, { useCallback, useEffect, useState } from 'react'
import { useLang } from '../i18n'
import { useAuth } from '../auth'

interface AuditEntry {
  id: string
  actor_id: string
  actor_role: string
  action: string
  entity_type: string
  entity_id: string
  diff: { before: unknown; after: unknown } | null
  created_at: string | null
}

interface PageInfo {
  page: number
  page_size: number
  total: number
}

const PAGE_SIZE = 50

export function AuditLogPage() {
  const { t } = useLang()
  const { accessToken } = useAuth()

  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [pageInfo, setPageInfo] = useState<PageInfo>({ page: 1, page_size: PAGE_SIZE, total: 0 })
  const [page, setPage] = useState(1)
  const [entityTypeFilter, setEntityTypeFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const fetchLogs = useCallback(async () => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) })
      if (entityTypeFilter) params.set('entity_type', entityTypeFilter)

      const resp = await fetch(`/api/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      const data = await resp.json()
      setEntries(data.data ?? [])
      setPageInfo(data.page ?? { page, page_size: PAGE_SIZE, total: 0 })
    } catch {
      setError(t('errorGeneric'))
    } finally {
      setLoading(false)
    }
  }, [accessToken, page, entityTypeFilter, t])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  const totalPages = Math.max(1, Math.ceil(pageInfo.total / PAGE_SIZE))

  const tdStyle: React.CSSProperties = {
    padding: '8px 10px',
    borderBottom: '1px solid var(--border)',
    fontSize: '12px',
    verticalAlign: 'top',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }

  return (
    <div style={{ maxWidth: '1400px' }}>
      <h2 style={{ marginBottom: '16px' }}>{t('navAuditLog')}</h2>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder={t('auditFilterEntityType')}
          value={entityTypeFilter}
          onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1) }}
          style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '13px', width: '200px' }}
        />
        <button
          onClick={() => { setEntityTypeFilter(''); setPage(1) }}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '13px', cursor: 'pointer' }}
        >
          {t('auditClearFilter')}
        </button>
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{t('auditTotal')}: {pageInfo.total}</span>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '12px', padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('loading')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: 'var(--bg-page)' }}>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditTime')}</th>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditActor')}</th>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditActorRole')}</th>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditAction')}</th>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditEntityType')}</th>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditEntityId')}</th>
                <th style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t('auditDiff')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                    {t('auditNoEntries')}
                  </td>
                </tr>
              )}
              {entries.map((entry) => (
                <tr key={entry.id} style={{ background: entry.actor_role === 'admin' ? 'var(--warning-bg)' : undefined }}>
                  <td style={tdStyle} title={entry.created_at ?? ''}>
                    {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                  </td>
                  <td style={tdStyle} title={entry.actor_id}>{entry.actor_id.slice(0, 8)}…</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block',
                      padding: '1px 6px',
                      borderRadius: '3px',
                      background: entry.actor_role === 'admin' ? 'var(--warning-bg)' : entry.actor_role === 'manager' ? 'var(--info-bg)' : 'var(--bg-elevated)',
                      fontSize: '11px',
                    }}>
                      {entry.actor_role}
                    </span>
                  </td>
                  <td style={tdStyle}>{entry.action}</td>
                  <td style={tdStyle}>{entry.entity_type}</td>
                  <td style={tdStyle} title={entry.entity_id}>{entry.entity_id.slice(0, 8)}…</td>
                  <td style={{ ...tdStyle, maxWidth: '300px' }}>
                    <button
                      onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                      style={{ fontSize: '11px', padding: '2px 6px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: '3px', background: 'var(--bg-surface)' }}
                    >
                      {expanded === entry.id ? '▲' : '▼'} diff
                    </button>
                    {expanded === entry.id && (
                      <pre style={{ marginTop: '6px', fontSize: '11px', background: 'var(--bg-elevated)', padding: '6px', borderRadius: '3px', overflowX: 'auto', whiteSpace: 'pre-wrap', maxWidth: '400px' }}>
                        {JSON.stringify(entry.diff, null, 2)}
                      </pre>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', alignItems: 'center' }}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '4px', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
        >
          ‹
        </button>
        <span style={{ fontSize: '13px' }}>{page} / {totalPages}</span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '4px', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
        >
          ›
        </button>
      </div>
    </div>
  )
}
